import { showNotification } from '@mantine/notifications';
import type { BackgroundJobOut } from '@repo/api-codegen';
import { useApiCompleteAutomationReview } from '@repo/api-codegen';
import { Icon } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { useMemo, useState } from 'react';
import { queryClient, type IApiListResponse } from '../../api';
import { useAutomationJobList } from '../../api/hooks/automationJob';
import { automationJobQueryKeyFactory } from '../../api/hooks/automationJob/constants.ts';
import { useBackgroundJob } from '../../api/hooks/backgroundJob/index.ts';
import type { AutomationJob } from '../../api/types/models/automation/automationJob';
import type { IBackgroundJob } from '../../api/types/models/backgroundJob.ts';
import type { BackgroundJob } from '../../lib/models/backgroundJob.ts';
import { useFeatureFlags } from '../../utils/featureFlags';
import { AutomationJobDrawerV2 } from '../AutomationV2/JobDrawer/AutomationJobDrawerV2.tsx';
import { BackgroundJobProgress } from '../BackgroundJobProgress/BackgroundJobProgress.tsx';
import { TableV2 } from '../TableV2';
import { AutomationJobDrawer } from './AutomationJobDrawer';
import { useColumns } from './hooks';
import { AUTOMATION_JOB_STATUS } from './utils.ts';

interface AutomationHistoryTableProps {
	automationId: string;
}

const COLUMN_VISIBILITY = {
	catalogType: 'automation_jobs',
	catalogServerType: 'automation_jobs',
} as const;

const DEFAULT_SORT = {
	field: 'created_at',
	order: 'desc' as const,
};

export function AutomationHistoryTable({
	automationId,
}: AutomationHistoryTableProps) {
	const [jobDetailShown, setJobDetailShown] = useState(false);
	const [
		isCompleteAutomationReviewLoading,
		setIsCompleteAutomationReviewLoading,
	] = useState<boolean>(false);
	const [completeAutomationReviewJobId, setCompleteAutomationReviewJobId] =
		useState<string | undefined>(undefined);
	const [backgroundJob, setBackgroundJob] = useState<BackgroundJobOut | null>(
		null
	);
	const [currentAutomationJob, setCurrentAutomationJob] =
		useState<AutomationJob | null>(null);
	const columns = useColumns();
	const { automationAiGeneration } = useFeatureFlags();

	const defaultSearchParams = useMemo(
		() => ({
			automation: automationId,
		}),
		[automationId]
	);

	const completeAutomationReviewMutation = useApiCompleteAutomationReview({
		onMutate: () => {
			setIsCompleteAutomationReviewLoading(true);
		},
		onSuccess: (response) => {
			// Response should contain the job ID
			if (response.background_job) {
				setBackgroundJob(response.background_job);
			} else {
				setIsCompleteAutomationReviewLoading(false);
			}
		},
		onError: () => {
			showNotification({
				title: 'Could not complete automation review',
				message: 'Please contact customer support if the issue continues',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		},
	});

	// Poll the background job until completion
	useBackgroundJob({
		id: backgroundJob?.id || '',
		options: {
			enabled:
				!isNil(backgroundJob?.id) && !isNil(completeAutomationReviewJobId),
			refetchInterval: (query) => {
				const { data } = query.state;

				if (data?.failed) {
					setIsCompleteAutomationReviewLoading(false);
					showNotification({
						title: 'Automation review completion failed',
						message: 'Please contact customer support if the issue continues',
						color: 'red',
						icon: (
							<Icon name="alertCircle" color="text/critical-on-fill/default" />
						),
					});
					setCompleteAutomationReviewJobId(undefined);
					return false;
				}

				if (data?.completed) {
					setIsCompleteAutomationReviewLoading(false);
					setCompleteAutomationReviewJobId(undefined);
					return false;
				}

				// Continue polling every second while job is running
				return 1000;
			},
		},
	});

	const handleBackgroundJobComplete = (result?: IBackgroundJob) => {
		if (result?.failed) {
			showNotification({
				title: 'Error running automation',
				message: 'An error has occurred while running the automation',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} else {
			showNotification({
				title: 'Automation changes applied successfully',
				message: 'Automation successfully applied changes after review',
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
				closeButtonProps: {
					'data-testid': 'automation-review-success-close-button',
				},
			});
		}

		setBackgroundJob(null);
		queryClient.invalidateQueries({
			queryKey: automationJobQueryKeyFactory.all(),
		});
	};

	const handleCompleteAutomationReview = (automationJobId: string) => {
		setCompleteAutomationReviewJobId(automationJobId);
		completeAutomationReviewMutation.mutate({
			pathParams: {
				automationJobId,
			},
		});
		setJobDetailShown(false);
		setCurrentAutomationJob(null);
	};

	return (
		<>
			<TableV2
				onRowClick={(automationJob: AutomationJob) => {
					setJobDetailShown(true);
					setCurrentAutomationJob(automationJob);
				}}
				columns={columns}
				usePaginationList={useAutomationJobList}
				usePaginationListOptions={{
					select: (response: IApiListResponse<AutomationJob>) => {
						const reverted: Record<string, boolean> = {};
						response.results.forEach((job) => {
							if (
								job.is_rollback &&
								job.job_rolled_back &&
								job.status === AUTOMATION_JOB_STATUS.COMPLETED
							) {
								reverted[job.job_rolled_back] = true;
							}
							if (reverted[job.id]) job.status = AUTOMATION_JOB_STATUS.REVERTED;
						});
						return response;
					},
				}}
				defaultRequiredSearchParams={defaultSearchParams}
				defaultSort={DEFAULT_SORT}
				columnVisibility={COLUMN_VISIBILITY}
				withInteractiveHeader={false}
			/>
			{currentAutomationJob &&
				(automationAiGeneration ? (
					<AutomationJobDrawerV2
						automationJob={currentAutomationJob}
						opened={jobDetailShown}
						onClose={() => {
							setJobDetailShown(false);
							setCurrentAutomationJob(null);
						}}
						handleCompleteAutomationReview={handleCompleteAutomationReview}
						isCompleteAutomationReviewLoading={
							isCompleteAutomationReviewLoading
						}
					/>
				) : (
					<AutomationJobDrawer
						automationJob={currentAutomationJob}
						opened={jobDetailShown}
						onClose={() => {
							setJobDetailShown(false);
							setCurrentAutomationJob(null);
						}}
					/>
				))}
			{backgroundJob && (
				<BackgroundJobProgress
					key={backgroundJob.id}
					job={backgroundJob as unknown as BackgroundJob}
					onCompleted={handleBackgroundJobComplete}
				/>
			)}
		</>
	);
}
